import React, { useState, useEffect } from 'react';
import { Avatar, Chip, Menu, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Box } from '@mui/system';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import GradingOutlinedIcon from '@mui/icons-material/GradingOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import GGButton from '../../../CommonComponents/GGButton/GGButton';
import GGMenuItem from '../../../CommonComponents/GGMenuItem/GGMenuItem';
import TimerClock from '../../../CommonComponents/TimerClock/TimerClock';
import GGCalender from '../../../CommonComponents/Calendar/GGCalender';
import GGToolTip from '../../../UtilityComponents/GGToolTip/GGToolTip';
import GGModalWindow from '../../../UtilityComponents/GGModalWindow/GGModalWindow';
import { getDateFromTimeStamp, getDateObjForCalendar } from '../../../../utils/DateAndTimeUtils';
import { Order, Revision as OrderRevision, OrderStatus } from '../../../../Models/Order';
import { formatCurrencyIndian } from '../../../../utils/MoneyConversionUtils';
import RevisionRequestCard from '../../ChatComponents/GGCustomMessages/RevisionRequestCard';
import './WorkspaceOrderCard.scss';

interface WorkspaceOrderCardProps {
    initialOrder: Order;
    onSubmitFinalWork: (order: Order) => void;
    onShareProgressReview: (order: Order) => void;
}

const WorkspaceOrderCard: React.FC<WorkspaceOrderCardProps> = ({
    initialOrder,
    onSubmitFinalWork,
    onShareProgressReview,
}) => {
    const navigate = useNavigate();
    const [newRequestAvailable, setNewRequestAvailable] = useState<boolean>(false);
    const [dueBy, setDueBy] = useState<string | null>('calendar');
    const [showRevisionModal, setShowRevisionModal] = useState(false);
    const [selectedRevision, setSelectedRevision] = useState<{ revisionId: string; revisionNumber: number }>({
        revisionId: '',
        revisionNumber: 0,
    });
    const [order, setOrder] = useState<Order>(initialOrder);
    const [showOrderSummary, setShowOrderSummary] = useState<boolean>(false);

    useEffect(() => {
        const validRevisions = order.revisionDetails?.validRevisions ?? [];
        // Check if any revision is unread (notSeen)
        const hasUnreadRevision = validRevisions.some(
            (revision) => revision.revisionState === 'notSeen'
        );
        setNewRequestAvailable(hasUnreadRevision);
    }, [order.revisionDetails?.validRevisions]);

    const handleDueType = (event: React.MouseEvent<HTMLElement>, newDueBy: string | null) => {
        if (newDueBy) setDueBy(newDueBy);
    };

    const deliveryDate = getDateObjForCalendar(order.deliveryTimeStamp ?? new Date());

    const getGrandTotalInINR = () => {
        let amountPayable = order.orderSummary?.orderTotals.totalNetAmountReleasedToSeller ?? 0;
        return formatCurrencyIndian(amountPayable);
    };

    const orderSummaryDetailsBtnHandler = () => {
        setShowOrderSummary(!showOrderSummary);
    };

    const closeOrderSummaryModal = () => {
        setShowOrderSummary(false);
    };

    const OrderSummaryModalContent = (
        <div className="order-summary-modal-content">
            <table className="order-summary-table" style={{ width: '100%' }}>
                <tbody>
                    <tr>
                        <td className="order-summary-section-title" colSpan={2}>
                            <b>Main Order Breakdown</b>
                        </td>
                    </tr>
                    <tr>
                        <td className="order-summary-item-label">Gig Amount</td>
                        <td className="order-summary-item-value">
                            {formatCurrencyIndian(order.orderSummary?.primaryTransaction.basePrice ?? 0)}
                        </td>
                    </tr>
                    <tr>
                        <td className="order-summary-item-label">
                            Platform Fee (
                            {(order.orderSummary?.primaryTransaction.sellerFinancials.platformFee.percentage ?? 0) *
                                100}
                            %)
                        </td>
                        <td className="order-summary-item-value deduction">
                            -{' '}
                            {formatCurrencyIndian(
                                order.orderSummary?.primaryTransaction.sellerFinancials.platformFee
                                    .actualFeeCharged ?? 0
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td className="order-summary-item-label">
                            GST @{' '}
                            {(order.orderSummary?.primaryTransaction.sellerFinancials.gstDetails.ratePercentage ??
                                0) *
                                100}
                            %
                        </td>
                        <td className="order-summary-item-value deduction">
                            -{' '}
                            {formatCurrencyIndian(
                                order.orderSummary?.primaryTransaction.sellerFinancials.gstDetails.actualGstAmount ??
                                    0
                            )}
                        </td>
                    </tr>
                    <tr className="order-summary-subtotal-row">
                        <td className="order-summary-subtotal-title">
                            <b>Main Order Subtotal</b>
                        </td>
                        <td className="order-summary-subtotal-value" style={{ textAlign: 'right' }}>
                            <b>
                                {formatCurrencyIndian(
                                    order.orderSummary?.primaryTransaction.sellerFinancials.totalAmountPayable ?? 0
                                )}
                            </b>
                        </td>
                    </tr>
                    {order.orderSummary?.revisionTransactions &&
                        order.orderSummary.revisionTransactions.length > 0 && (
                            <>
                                <tr>
                                    <td colSpan={2}>
                                        <hr className="order-summary-section-divider" />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="order-summary-section-title" colSpan={2}>
                                        <b>Revision Transactions</b>
                                    </td>
                                </tr>
                                {order.orderSummary.revisionTransactions.map((revision, index) => {
                                    const isFreeRevision = revision.buyerFinancials.totalAmountPayable === 0;
                                    const revisionNumber = index + 1;

                                    return (
                                        <React.Fragment key={revision.transactionId}>
                                            <tr className="revision-spacer">
                                                <td colSpan={2}></td>
                                            </tr>
                                            {isFreeRevision ? (
                                                <tr>
                                                    <td className="order-summary-item-label">
                                                        Included Revision #{revisionNumber}
                                                    </td>
                                                    <td className="order-summary-item-value free">FREE</td>
                                                </tr>
                                            ) : (
                                                <>
                                                    <tr>
                                                        <td className="order-summary-item-label">
                                                            Paid Revision #{revisionNumber} - Base Amount
                                                        </td>
                                                        <td className="order-summary-item-value">
                                                            {formatCurrencyIndian(revision.basePrice)}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="order-summary-item-label">
                                                            Platform Fee ({revision.sellerFinancials.platformFee.percentage * 100}%)
                                                        </td>
                                                        <td className="order-summary-item-value deduction">
                                                            - {formatCurrencyIndian(revision.sellerFinancials.platformFee.actualFeeCharged)}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="order-summary-item-label">
                                                            GST @ {revision.sellerFinancials.gstDetails.ratePercentage * 100}%
                                                        </td>
                                                        <td className="order-summary-item-value deduction">
                                                            - {formatCurrencyIndian(revision.sellerFinancials.gstDetails.actualGstAmount)}
                                                        </td>
                                                    </tr>
                                                    <tr className="order-summary-revision-subtotal-row">
                                                        <td className="order-summary-revision-subtotal-title">
                                                            <b>Revision #{revisionNumber} Subtotal</b>
                                                        </td>
                                                        <td className="order-summary-revision-subtotal-value" style={{ textAlign: 'right' }}>
                                                            <b>{formatCurrencyIndian(revision.sellerFinancials.totalAmountPayable)}</b>
                                                        </td>
                                                    </tr>
                                                </>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </>
                        )}
                    <tr>
                        <td colSpan={2}>
                            <hr className="order-summary-divider" />
                        </td>
                    </tr>
                    <tr className="order-summary-grand-total-row">
                        <td className="order-summary-grand-total-title">
                            <b>Total Amount to be credited </b>
                        </td>
                        <td className="order-summary-grand-total-value" style={{ textAlign: 'right' }}>
                            <b>{getGrandTotalInINR()}</b>
                        </td>
                    </tr>
                </tbody>
            </table>
            <br></br>
        </div>
    );

    const OrderCardLeftPanel = () => {
        return (
            <Box className="workspace-order-card-left">
                <div className="order-id-and-date">
                    <Typography className="order-id">Order Id: #{order.id}</Typography>
                    <Typography className="order-date">{getDateFromTimeStamp(order.createdAt ?? new Date())}</Typography>
                </div>

                <div className="client-section">
                    <Avatar alt="" src={order.metaData.buyer.profileImage} className="client-avatar" />
                    <div className="client-info">
                        <Typography className="client-name">{order.metaData.buyer.fullName}</Typography>
                        <Typography className="member-since">
                            Member since {getDateFromTimeStamp(order.metaData.buyer.createdAt)}
                        </Typography>
                    </div>
                </div>

                <div className="service-section">
                    <Typography className="service-title">{order.metaData.gig.title}</Typography>
                    <div className="amount-section">
                        <Typography className="amount-label">
                            Amount to be credited <b>{getGrandTotalInINR()}</b>
                        </Typography>
                        <GGButton className="details-link" variant="text" onClick={orderSummaryDetailsBtnHandler} disableRipple>
                            <u>Details</u>
                        </GGButton>
                    </div>
                </div>

                <GGButton
                    className="go-to-chat-button"
                    disableClickShrink
                    onClick={() => navigate('/freelancer-dashboard/messages', { state: { channelId: order.metaData.channelId } })}
                    variant="outlined"
                    startIcon={<ChatOutlinedIcon />}
                >
                    Go to chat
                </GGButton>

                <GGModalWindow
                    childView={OrderSummaryModalContent}
                    open={showOrderSummary}
                    title={`Order Summary : ID: #${order.id}`}
                    onCloseHandler={closeOrderSummaryModal}
                />
            </Box>
        );
    };

    const OrderCardRightPanel = () => {
        let content: React.ReactNode;

        switch (order.status) {
            case OrderStatus.readyForDelivery:
                content = <PendingBuyerReview />;
                break;

            case OrderStatus.approvedByBuyer:
                content = <PaymentReleasePending />;
                break;

            case OrderStatus.overdue:
                content = <OrderOverdue />;
                break;

            case OrderStatus.pending:
                content = <OrderActionsSection />;
                break;

            default:
                content = (
                    <div className="order-status-text">
                        Order Status: {order.status} Please contact Support for help!
                    </div>
                );
                break;
        }

        return (
            <Box className={`workspace-order-card-right ${newRequestAvailable ? 'show-red-border' : ''}`}>
                {content}
            </Box>
        );
    };

    const OrderOverdue = () => (
        <div className="order-overdue">
            <Typography variant="subtitle1" fontWeight={'bold'} marginTop={'10px'} color={'red'}>
                Order is Overdue!
            </Typography>
            <Typography variant="subtitle1">
                Was due on: <b>{deliveryDate.day}-{deliveryDate.month}-{deliveryDate.year}</b>
            </Typography>
            <OrderActionButtons></OrderActionButtons>
        </div>
    );

    const PaymentReleasePending = () => (
        <div className="payment-release-pending">
            <CheckCircleIcon className="success-icon" />
            <Typography variant="subtitle1" fontWeight={'bold'}>
                Approval Success!
            </Typography>
            <Typography variant="subtitle1">Payment is pending to be released by GenieGig</Typography>
            <GGToolTip
                title="How long can it take?"
                body="The payment will be released within 24 hours after the order is marked as completed. If you have any issues, please contact our support team. (Please note that once your payment is released, this order will be automatically moved to your orders section)"
            >
                <Chip
                    className="info-chip"
                    label="How long can it take?"
                    variant="outlined"
                    color="info"
                    style={{ backgroundColor: 'white' }}
                />
            </GGToolTip>
        </div>
    );

    const PendingBuyerReview = () => (
        <div className="pending-approval-from-client">
            <CheckCircleIcon className="success-icon" />
            <Typography variant="subtitle1">You've completed your part 🥳</Typography>
            <Typography variant="subtitle1">
                Review Pending by <b>{order.metaData.buyer.fullName}</b>
            </Typography>
            <GGToolTip
                title="How long can it take?"
                body="The client has 48 hrs days to review your work during which, they can either accept or ask for revision. If they don't respond within that time, the order will be automatically marked as completed. (Meanwhile, We are doing our part to make sure that the client is not taking too long to review your work)"
            >
                <Chip
                    className="info-chip"
                    label="How long can it take?"
                    variant="outlined"
                    color="info"
                    style={{ backgroundColor: 'white' }}
                />
            </GGToolTip>
        </div>
    );

    const OrderActionsSection = () => (
        <div className="order-actions-container">
            <div className="due-by-section">
                <Typography className="due-by-label">Due by</Typography>
                {dueBy === 'calendar' ? (
                    <GGCalender
                        className="gg-calendar"
                        day={deliveryDate.day}
                        month={deliveryDate.month}
                        year={deliveryDate.year}
                    />
                ) : (
                    <TimerClock createdAt={order.deliveryTimeStamp ?? new Date()} />
                )}
                <ToggleButtonGroup
                    className="due-toggle-button-grp"
                    value={dueBy}
                    exclusive
                    onChange={handleDueType}
                    aria-label="due-type"
                >
                    <ToggleButton className="toggle-button" value="calendar">
                        <CalendarMonthOutlinedIcon className="toggle-button-icon" />
                    </ToggleButton>
                    <ToggleButton className="toggle-button" value="clock">
                        <ScheduleRoundedIcon className="toggle-button-icon" />
                    </ToggleButton>
                </ToggleButtonGroup>
            </div>
            <OrderActionButtons />
        </div>
    );

    const OrderActionButtons = () => {
        const [anchorElForRevisionDropDown, setAnchorEl] = useState<null | HTMLElement>(null);

        const openRevisionRequestDropDownMenu = Boolean(anchorElForRevisionDropDown);
        const handleViewRevReqBtnClick = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
        };
        const handlePopupMenuClose = () => {
            setAnchorEl(null);
        };

        const handleRevisionReqMenuItemClick = (revision: OrderRevision, revNo: number) => {
            setShowRevisionModal(true);
            setSelectedRevision({
                revisionId: revision.revisionId,
                revisionNumber: revNo,
            });
            handlePopupMenuClose();
        };

        const handleRevisionReqModalClose = async () => {
            setShowRevisionModal(false);
            
            // Mark only the selected revision as read locally
            if (selectedRevision.revisionId && order.revisionDetails?.validRevisions) {
                const updatedRevisions = order.revisionDetails.validRevisions.map((revision) => {
                    if (revision.revisionId === selectedRevision.revisionId) {
                        return { ...revision, revisionState: 'seen' as const };
                    }
                    return revision;
                });
                
                const updatedOrder = {
                    ...order,
                    revisionDetails: {
                        ...order.revisionDetails,
                        validRevisions: updatedRevisions,
                    },
                };
                setOrder(updatedOrder as Order);
            }
            
            setSelectedRevision({ revisionId: '', revisionNumber: 0 });
            handlePopupMenuClose();
        };

        return (
            <div className="order-action-buttons-section">
                {(order.revisionDetails?.validRevisions.length ?? 0) > 0 && (
                    <>
                        <div className="revision-request-btn-div">
                            {newRequestAvailable && <div className="red-dot red-dot-visible"></div>}
                            <GGButton
                                disableClickShrink
                                onClick={handleViewRevReqBtnClick}
                                className="view-review-request-btn"
                                variant="contained"
                                size="small"
                                endIcon={<KeyboardArrowDownIcon />}
                            >
                                View Revision Request
                            </GGButton>
                        </div>

                        <Menu
                            id="review-requests-menu"
                            anchorEl={anchorElForRevisionDropDown}
                            open={openRevisionRequestDropDownMenu}
                            onClose={handlePopupMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                                style: {
                                    width: anchorElForRevisionDropDown?.offsetWidth || undefined,
                                },
                            }}
                        >
                            {order.revisionDetails?.validRevisions.map((revision: OrderRevision, index: number) => (
                                <GGMenuItem
                                    key={index}
                                    showNewBadge={revision.revisionState === 'notSeen'}
                                    className="review-request-menu-item"
                                    onClick={() => {
                                        handleRevisionReqMenuItemClick(revision, index + 1);
                                    }}
                                >
                                    {`Revision Request ${index + 1}`}
                                </GGMenuItem>
                            ))}
                        </Menu>

                        <GGModalWindow
                            open={showRevisionModal}
                            onCloseHandler={handleRevisionReqModalClose}
                            title={`Revision Request ${selectedRevision.revisionNumber}`}
                            childView={
                                <div
                                    className="revision-request-modal-content"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    <RevisionRequestCard revisionId={selectedRevision.revisionId} />
                                    <GGButton
                                        className="Go to Chat"
                                        variant="outlined"
                                        btnColor="#0e0e0e"
                                        startIcon={<ChatOutlinedIcon />}
                                        style={{ marginTop: '10px' }}
                                        onClick={() =>
                                            navigate('/freelancer-dashboard/messages', {
                                                state: { channelId: order.metaData.channelId },
                                            })
                                        }
                                    >
                                        Go to Chat
                                    </GGButton>
                                </div>
                            }
                        />
                    </>
                )}

                <GGButton
                    onClick={() => onShareProgressReview(order)}
                    className="share-progress-review-button"
                    variant="contained"
                    size="small"
                    startIcon={<GradingOutlinedIcon />}
                >
                    Share Progress Review
                </GGButton>

                <GGButton
                    onClick={() => onSubmitFinalWork(order)}
                    className="submit-final-work-button"
                    variant="contained"
                    size="small"
                    startIcon={<AssignmentTurnedInOutlinedIcon />}
                >
                    Submit Final Work
                </GGButton>
            </div>
        );
    };

    // Only show red border for pending orders with unread revision requests
    const shouldShowHighlightBorder = order.status === OrderStatus.pending && newRequestAvailable;

    return (
        <Box className={`workspace-order-card ${shouldShowHighlightBorder ? 'highlight-border' : ''}`}>
            <OrderCardLeftPanel />
            <OrderCardRightPanel />
        </Box>
    );
};

export default WorkspaceOrderCard;

